import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Form, Row, Table } from "react-bootstrap";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { useLoaderData, useLocation, useNavigate } from "react-router";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { alertActions } from "../../features/alert/alertSlice";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { createField, getField, updateField } from "../../lib/api";
import { displayFormAlert } from "../../lib/utils";
import { IField } from "../../types/api";
import { ILeagueMgmtFieldData } from "../../types/forms";

export const loader = async ({ params }: { params: any }) => {
    const { fieldId } = params;

    const field = fieldId ? await getField(fieldId) : await Promise.resolve();

    return {
        header: `${field ? `Edit ${field.field?.name}` : "Add Field"}`,
        field: field?.field,
    };
};

const Field = (): React.JSX.Element => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isAddingField = /fields\/add\/?$/.test(location.pathname);

    const { field } = useLoaderData() as {
        field: IField | undefined;
    };

    const fieldFormSchema = yup.object().shape({
        name: yup.string().required("Please specify the field name"),
        address: yup.string().required("Please provide the field address"),
        city: yup.string().required("Please provide the field city"),
        state: yup.string().required("Please provide the field state"),
        zip: yup.string().required("Please provide the field zip code"),
        active: yup.number().required().oneOf([0, 1]),
        diamonds: yup
            .array(
                yup.object().shape({
                    id: yup.number().required(),
                    name: yup
                        .string()
                        .required(
                            "Please provide a diamond name or delete if not needed",
                        ),
                }),
            )
            .optional(),
    });

    const {
        register,
        formState: { errors },
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
    } = useForm<ILeagueMgmtFieldData>({
        mode: "onChange",
        resolver: yupResolver(fieldFormSchema),
        defaultValues: {
            diamonds:
                field?.diamonds && Object.keys(field.diamonds).length !== 0
                    ? field.diamonds.map(d => ({
                          id: d.id,
                          name: d.name,
                      }))
                    : [],
        },
    });

    const {
        fields: formFields,
        append,
        remove,
    } = useFieldArray({
        control,
        name: "diamonds",
    });

    const onSubmit: SubmitHandler<ILeagueMgmtFieldData> = async data => {
        const fieldPromise = isAddingField
            ? createField(data)
            : updateField(data, field!.id);

        await fieldPromise
            .then(response => {
                const alertAction = isAddingField ? "created" : "updated";

                dispatch(
                    alertActions.success({
                        title: `Field ${alertAction} successfully`,
                        dismissible: true,
                    }),
                );
                navigate("/league-management/fields");
            })
            .catch(e => {
                const alertAction = isAddingField ? "creating" : "updating";

                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem ${alertAction} the field.`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            });
    };

    const isFieldActive = isAddingField
        ? watch("active", 1)
        : watch("active", field?.active);

    const watchDiamonds = watch("diamonds");

    const mapAddress = encodeURIComponent(
        `${field?.address} ${field?.city}, ${field?.state}`,
    );

    return (
        <section className="fieldContent" data-testid="fieldContent">
            <Form onSubmit={handleSubmit(onSubmit)} id="leagueMgmtOrgInfoForm">
                <Row>
                    <Col>
                        <Form.Group controlId="name">
                            <Form.Label>Field Name</Form.Label>
                            <Form.Control
                                type="text"
                                {...register("name", {
                                    value: field?.name,
                                })}
                                aria-invalid={errors.name ? "true" : "false"}
                            />
                            {errors.name &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.name.message as string,
                                })}
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Label>Diamond Names</Form.Label>
                        <Table>
                            <tbody>
                                {formFields.map((diamond, index) => (
                                    <tr key={diamond.id}>
                                        <td width={"75%"}>
                                            <Form.Control
                                                type="text"
                                                {...register(
                                                    `diamonds.${index}.name` as const,
                                                )}
                                                placeholder="Diamond name or number"
                                                aria-invalid={
                                                    errors.diamonds &&
                                                    errors.diamonds[index]
                                                        ? "true"
                                                        : "false"
                                                }
                                            />
                                            {errors.diamonds &&
                                                errors.diamonds[index] &&
                                                displayFormAlert({
                                                    level: "danger",
                                                    text: errors.diamonds[index]
                                                        ?.name
                                                        ?.message as string,
                                                })}
                                        </td>
                                        <td>
                                            <Button
                                                type="button"
                                                className="no-margin-btn"
                                                onClick={() => remove(index)}
                                            >
                                                Delete Diamond
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <Button
                            type="button"
                            onClick={() =>
                                append({
                                    id: watchDiamonds
                                        ? watchDiamonds.reduce(
                                              (m, d) => (d.id > m ? d.id : m),
                                              0,
                                          ) + 1
                                        : 1,
                                    name: "",
                                })
                            }
                        >
                            Add New Diamond
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Form.Group controlId="address" as={Col} md={5}>
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("address", {
                                value: field?.address,
                            })}
                            aria-invalid={errors.address ? "true" : "false"}
                        />
                        {errors.address &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.address.message as string,
                            })}
                    </Form.Group>
                    <Form.Group controlId="city" as={Col} md={3}>
                        <Form.Label>City</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("city", {
                                value: field?.city,
                            })}
                            aria-invalid={errors.city ? "true" : "false"}
                        />
                        {errors.city &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.city.message as string,
                            })}
                    </Form.Group>
                    <Form.Group controlId="state" as={Col} md={2}>
                        <Form.Label>State</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("state", {
                                value: field?.state,
                            })}
                            aria-invalid={errors.state ? "true" : "false"}
                        />
                        {errors.state &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.state.message as string,
                            })}
                    </Form.Group>
                    <Form.Group controlId="zip" as={Col} md={2}>
                        <Form.Label>Zip Code</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("zip", {
                                value: field?.zip,
                            })}
                            aria-invalid={errors.zip ? "true" : "false"}
                        />
                        {errors.zip &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.zip.message as string,
                            })}
                    </Form.Group>
                </Row>
                <Row>
                    <Col>
                        <Form.Group
                            controlId="active"
                            as={Col}
                            md={4}
                            className="col"
                        >
                            <Form.Control
                                type="hidden"
                                {...register("active", {
                                    value: isAddingField
                                        ? 1
                                        : getValues("active") || field?.active,
                                })}
                            />
                            <ButtonGroup>
                                <Button
                                    variant={
                                        isFieldActive
                                            ? "primary"
                                            : "outline-primary"
                                    }
                                    onClick={() => setValue("active", 1)}
                                >
                                    Active
                                </Button>
                                <Button
                                    variant={
                                        isFieldActive
                                            ? "outline-primary"
                                            : "primary"
                                    }
                                    onClick={() => setValue("active", 0)}
                                >
                                    Inactive
                                </Button>
                            </ButtonGroup>
                        </Form.Group>
                    </Col>
                </Row>
                {!isAddingField && (
                    <Row className="mt-4">
                        <Col>
                            <iframe
                                data-testid="map"
                                width="100%"
                                height="500"
                                style={{ border: 0 }}
                                referrerPolicy="no-referrer-when-downgrade"
                                src={`https://www.google.com/maps/embed/v1/place/?key=${process.env.GOOGLE_MAPS_API_KEY}&q=${mapAddress}`}
                            ></iframe>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col>
                        <Button variant="primary" type="submit">
                            {isAddingField ? "Add Field" : "Update Field"}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </section>
    );
};

export default Field;
